import { Heading } from '../../../components/Heading';
import { Input, InputGroup } from '../../../components/Input';
import Loading from '../../../components/Loading';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/Table';
import dateFormat from 'dateformat';
import { useStockOut } from '../../../hooks/useStockOut';
import { MagnifyingGlassIcon } from '@heroicons/react/16/solid';
import { useContext, useEffect, useState } from 'react';
import { useTrackingPickup } from '../../../hooks/useTrackingPickup';
import { TrackingPickup } from '../../../models/TrackingPickup';
import { UserContext } from '../../../context/userContext';
import { StockOut } from '../../../models/StockOut';
import { useWarehouses } from '../../../hooks/useWarehouse';
import { Text } from '../../../components/Text';
import { Card } from '../../../components/Card';
import { ListToolbarRow, FiltersButton, SortMenu, ColumnsMenu, isColumnVisible } from '../../../components/ListToolbar';
import { FiltersDialog, FilterPills, FilterPill } from '../../../components/FiltersDialog';
import { DateRangePicker } from '../../../components/DateRangePicker';
import { Select } from '../../../components/Select';

const COLUMNS = [
    { key: 'customer', label: 'Customer' },
    { key: 'invoice', label: 'Invoice No' },
    { key: 'date', label: 'Date' },
    { key: 'driver', label: 'Driver Name' },
    { key: 'vehicle', label: 'Vehicle No' },
    { key: 'pickupDate', label: 'Pickup Date' },
    { key: 'pickupTime', label: 'Pickup Time' },
];
const SORT_OPTIONS = [
    { label: 'Newest first', value: 'newest' },
    { label: 'Oldest first', value: 'oldest' },
];

const CompletedStockOutPage: React.FC = () => {
    const { currentUser } = useContext(UserContext);
    const { loading, error, fetchCompletedStockOuts, fetchCompletedStockOutsByWarehouseId } = useStockOut();
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [trackingPickups, setTrackingPickups] = useState<Record<number, TrackingPickup | null>>({});
    const [completedStockOutList, setCompletedStockOutList] = useState<StockOut[]>([]);

    const { warehouses } = useWarehouses();
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [sortOrder, setSortOrder] = useState('newest');
    const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({});
    const toggleColumn = (key: string) => setColumnVisibility((prev) => ({ ...prev, [key]: prev[key] === false ? true : false }));
    const [filterStartDate, setFilterStartDate] = useState('');
    const [filterEndDate, setFilterEndDate] = useState('');
    const [filterYear, setFilterYear] = useState('');
    const [filterWarehouseId, setFilterWarehouseId] = useState('');

    const resetFilters = () => {
        setFilterStartDate('');
        setFilterEndDate('');
        setFilterYear('');
        setFilterWarehouseId('');
    };

    const availableYears = Array.from(
        new Set(
            completedStockOutList
                .map((s) => new Date(s.created_time).getFullYear())
                .filter((year) => !Number.isNaN(year))
        )
    ).sort((a, b) => b - a);

    const activeFilterCount = [filterStartDate, filterEndDate, filterYear, filterWarehouseId].filter(Boolean).length;

    const filterPills: FilterPill[] = [
        ...(filterStartDate ? [{ key: 'from', label: `From: ${filterStartDate}`, onClear: () => setFilterStartDate('') }] : []),
        ...(filterEndDate ? [{ key: 'to', label: `To: ${filterEndDate}`, onClear: () => setFilterEndDate('') }] : []),
        ...(filterYear ? [{ key: 'year', label: `Year: ${filterYear}`, onClear: () => setFilterYear('') }] : []),
        ...(filterWarehouseId ? [{
            key: 'warehouse',
            label: `Warehouse: ${warehouses?.find((w) => w.id?.toString() === filterWarehouseId)?.warehouse_name || filterWarehouseId}`,
            onClear: () => setFilterWarehouseId(''),
        }] : []),
    ];

    useEffect(() => {
        document.title = "Completed Stock Out Delivery";
    }, []);

    // Fetch stockOuts based on user's HQ status
    useEffect(() => {
        const fetchStockOutList = async () => {
            try {
                let fetchedStockOuts;
                // Check if user has HQ access
                if (currentUser?.warehouse_hq === "1") {
                    fetchedStockOuts = await fetchCompletedStockOuts();
                    setCompletedStockOutList(fetchedStockOuts || []); // Set the fetched stockOuts to the state

                } else if (currentUser?.warehouse_id && currentUser.role!.toString() !== "1") {
                    fetchedStockOuts = await fetchCompletedStockOutsByWarehouseId(currentUser.warehouse_id.toString());
                    setCompletedStockOutList(fetchedStockOuts || []); // Set the fetched stockOuts to the state
                }
            } catch (error) {
                console.error('Failed to fetch stock outs:', error);
            }
        };
        fetchStockOutList();
    }, [currentUser]); // Dependencies to re-run on change

    const filteredStockOut = Array.isArray(completedStockOutList)
        ? completedStockOutList.filter((stockOut) => {
            const matchesSearch = stockOut.invoice_no?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                stockOut.reference_no?.toLowerCase().includes(searchQuery.toLowerCase())
            const matchesStartDate = !filterStartDate || new Date(stockOut.created_time) >= new Date(filterStartDate);
            const matchesEndDate = !filterEndDate || new Date(stockOut.created_time) <= new Date(filterEndDate);
            const matchesYear = !filterYear || new Date(stockOut.created_time).getFullYear().toString() === filterYear;
            const matchesWarehouse = !filterWarehouseId || stockOut.warehouse_id === Number(filterWarehouseId);

            return matchesSearch && matchesStartDate && matchesEndDate && matchesYear && matchesWarehouse;
        }) : [];

    return (
        <>
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div className="max-sm:w-full sm:flex-1">
                    <Heading>Completed</Heading>
                    <div className="mt-4 flex max-w-xl gap-4">
                        <div className="flex-1">
                            <InputGroup>
                                <MagnifyingGlassIcon />
                                <Input
                                    name="search"
                                    placeholder="Search completed&hellip;"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />
                            </InputGroup>
                        </div>
                    </div>
                </div>
            </div>

            <Card className="mt-6 p-5">
            <ListToolbarRow>
                <FiltersButton count={activeFilterCount} onClick={() => setIsFilterOpen(true)} />
                <SortMenu options={SORT_OPTIONS} value={sortOrder} onChange={setSortOrder} />
                <ColumnsMenu columns={COLUMNS} visible={columnVisibility} onToggle={toggleColumn} />
            </ListToolbarRow>
            <FilterPills pills={filterPills} onClearAll={resetFilters} />

            <ul>
                {loading && <div className='w-full text-center h-full py-36 justify-center'><Loading /></div>}
                {error && <p className='py-16 text-center text-red-700'>{error}</p>}
                {(!loading && !error) && (
                    <div>
                        <Table className="[--gutter:theme(spacing.6)] lg:[--gutter:theme(spacing.10)]">
                            <TableHead>
                                <TableRow>
                                    <TableHeader>S/N</TableHeader>
                                    <TableHeader>Reference No</TableHeader>
                                    {isColumnVisible(columnVisibility, 'customer') && <TableHeader>Customer</TableHeader>}
                                    {isColumnVisible(columnVisibility, 'invoice') && <TableHeader>Invoice No</TableHeader>}
                                    {isColumnVisible(columnVisibility, 'date') && <TableHeader>Date</TableHeader>}
                                    {isColumnVisible(columnVisibility, 'driver') && <TableHeader>Driver Name</TableHeader>}
                                    {isColumnVisible(columnVisibility, 'vehicle') && <TableHeader>Vehicle No</TableHeader>}
                                    {isColumnVisible(columnVisibility, 'pickupDate') && <TableHeader>Pickup Date</TableHeader>}
                                    {isColumnVisible(columnVisibility, 'pickupTime') && <TableHeader>Pickup Time</TableHeader>}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(filteredStockOut.length > 0) ? [...filteredStockOut].sort((a, b) => {
                                    const diff = new Date(a.created_time).getTime() - new Date(b.created_time).getTime();
                                    return sortOrder === 'newest' ? -diff : diff;
                                }).map((stockOut, index) => {
                                    const pickup = trackingPickups[stockOut.id] || null; // Get the corresponding pickup
                                    return (
                                        <TableRow key={stockOut.id} title={`${stockOut.reference_no}`}>
                                            <TableCell>{index + 1}</TableCell>
                                            <TableCell>{stockOut.reference_no}</TableCell>
                                            {isColumnVisible(columnVisibility, 'customer') && <TableCell className="text-zinc-500">{stockOut.customer_fullname}</TableCell>}
                                            {isColumnVisible(columnVisibility, 'invoice') && <TableCell className="text-zinc-500">{stockOut.invoice_no}</TableCell>}
                                            {isColumnVisible(columnVisibility, 'date') && <TableCell className="text-zinc-500">{dateFormat(stockOut.date?.toString(), "mmmm dd, yyyy")}</TableCell>}
                                            {isColumnVisible(columnVisibility, 'driver') && <TableCell className="text-zinc-500">{stockOut.driver_name || '-'}</TableCell>}
                                            {isColumnVisible(columnVisibility, 'vehicle') && <TableCell className="text-zinc-500">{stockOut.vehicle_no || '-'}</TableCell>}
                                            {isColumnVisible(columnVisibility, 'pickupDate') && <TableCell>{dateFormat(stockOut.pickup_updated_time?.toString(), "mmmm dd, yyyy")}</TableCell>}
                                            {isColumnVisible(columnVisibility, 'pickupTime') && <TableCell>{dateFormat(stockOut.pickup_updated_time?.toString(), "hh:mm:ss")}</TableCell>}
                                        </TableRow>
                                    );
                                }) : <TableRow>
                                    <TableCell colSpan={9} className="text-center py-8">No stock out found.</TableCell>
                                </TableRow>}
                            </TableBody>
                        </Table>
                    </div>
                )}
            </ul>
            </Card>

            <FiltersDialog open={isFilterOpen} onClose={() => setIsFilterOpen(false)} onReset={resetFilters}>
                <div className="sm:col-span-2">
                    <Text>Date Range</Text>
                    <DateRangePicker
                        from={filterStartDate}
                        to={filterEndDate}
                        onChange={({ from, to }) => { setFilterStartDate(from); setFilterEndDate(to); }}
                        className="w-full"
                    />
                </div>
                <div>
                    <Text>By Year</Text>
                    <Select value={filterYear} onChange={(e) => setFilterYear(e.target.value)}>
                        <option value="">All Years</option>
                        {availableYears.map((year) => (
                            <option key={year} value={year.toString()}>{year}</option>
                        ))}
                    </Select>
                </div>
                {(currentUser?.role.toString() === "1") && (
                    <div>
                        <Text>By Warehouse</Text>
                        <Select value={filterWarehouseId} onChange={(e) => setFilterWarehouseId(e.target.value)}>
                            <option value="">All Warehouses</option>
                            {warehouses?.map((warehouse) => (
                                <option key={warehouse.id} value={warehouse.id?.toString()}>
                                    {warehouse.warehouse_name}
                                </option>
                            ))}
                        </Select>
                    </div>
                )}
            </FiltersDialog>
        </>
    );
};

export default CompletedStockOutPage;